import Swal from "sweetalert2";
import PropTypes from 'prop-types';
import { Col } from 'react-bootstrap';
import styled from 'styled-components';
import EditIcon from 'mdi-react/EditIcon';
import Badge from '@/shared/components/Badge';
import React, { useEffect, useState } from 'react';
import AdminInterface from '@/utils/AdminInterface';
import withReactContent from "sweetalert2-react-content";
import DeleteForeverIcon from 'mdi-react/DeleteForeverIcon';
import { paddingLeft, paddingRight } from '@/utils/directions';
import { colorAdditional, colorRedHover } from '@/utils/palette';
import { colorFieldsBorder, colorGreenHover } from '@/utils/palette';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import { Card, CardBody, CardTitleWrap, CardTitle } from '@/shared/components/Card';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  position: relative;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  &:hover {
    color: ${colorRedHover};

    svg {
      fill: ${colorRedHover};
    }
  }

  &:hover .edit {
    color: ${colorGreenHover};

    svg {
      fill: ${colorGreenHover};
    }
  }
`;

const ProductImageWrap = styled.div`
  width: 40px;
  height: 35px;
  text-align: center;
  padding: 2px;
  border: 1px solid ${colorFieldsBorder};

  img {
    max-height: 100%;
    width: auto;
  }
`;

const StatusFormatter = value => (
  value ? <Badge bg="success">Enable</Badge> : <Badge bg="secondary">Disable</Badge>
);

StatusFormatter.propTypes = {
  value: PropTypes.bool.isRequired,
};

const PhotoFormatter = value => (
  <ProductImageWrap>
    <img src={value} alt="" />
  </ProductImageWrap>
);

const UsersList = ({setEditUserIdHandler}) => {
  const [usersList, setUsersList] = useState({ tableHeaderData: [], tableRowsData: [] });
  const tableConfig = {
    isEditable: false,
    isSortable: true,
    isResizable: false,
    withPagination: true,
    withSearchEngine: true,
    manualPageSize: [10, 20, 30, 40],
    placeholder: 'Search...',
  };

  const deleteUser = (user_id) => {
    AdminInterface.instance.deleteUser(user_id).then((response) => {
      Swal.fire('Deleted!', '', 'success')
    }).catch((error) => {
      withReactContent(Swal).fire({
        title: "Error",
        html: error.response.data?.detail,
      });
    });
  }

  const handleDelete = (user_id) => {
    Swal.fire({
      title: 'Do you want to permanently delete the user?',
      showDenyButton: true,
      confirmButtonText: 'Yes',
      denyButtonText: 'No'
    }).then((result) => {
      if (result.isConfirmed) {
        deleteUser(user_id);
      }
    })
  }

  const handleEdit = (user_id) => {
    setEditUserIdHandler(user_id)
  }

  const GetUsersData = () => {
    AdminInterface.instance.getUsers().then((response) => {
      const columns = [
        {
          accessor: 'picture',
          Header: 'Picture',
          disableGlobalFilter: true,
          disableSortBy: true,
        },
        {
          accessor: 'name',
          Header: 'Name'
        },
        {
          accessor: 'email',
          Header: 'Email'
        },
        {
          accessor: 'plan',
          Header: 'Plan'
        },
        {
          accessor: 'blocked',
          Header: 'Blocked',
          disableGlobalFilter: true
        },
        {
          accessor: 'isManager',
          Header: 'Is Manager?',
          disableGlobalFilter: true
        },
        {
          accessor: 'edit',
          Header: 'Edit',
          disableGlobalFilter: true
        },
        {
          accessor: 'delete',
          Header: 'Delete',
          disableGlobalFilter: true
        }
      ];
      const data = [];
      response.data.forEach(user => {
        data.push({
          picture: PhotoFormatter(user.picture),
          id: user.user_id,
          name: `${user.given_name} ${user.family_name}`,
          email: user.email,
          plan: user.plan,
          blocked: StatusFormatter(user.blocked),
          isManager: StatusFormatter(user.isManager),
          edit: <CartTableButton className="edit" type="button" onClick={() => handleEdit(user.user_id)}><EditIcon /></CartTableButton>,
          delete: <CartTableButton type="button" onClick={() => handleDelete(user.user_id)}><DeleteForeverIcon /></CartTableButton>,
        });
      });

      const usersListTableData = { tableHeaderData: columns, tableRowsData: data };
      setUsersList(usersListTableData);
    }).catch((error) => {
      withReactContent(Swal).fire({
        title: "Error",
        html: error.response.data?.detail,
      });
    })
  };

  useEffect(() => {
    GetUsersData();
  }, []);

  return (
    <Col md={12} lg={12}>
      <Card>
        <CardBody>
          <CardTitleWrap>
            <CardTitle>Users list</CardTitle>
          </CardTitleWrap>
          <ReactTableBase
            columns={usersList.tableHeaderData}
            data={usersList.tableRowsData}
            tableConfig={tableConfig}
          />
        </CardBody>
      </Card>
    </Col>
  );
};

UsersList.propTypes = {
  usersListTableData: PropTypes.shape({
    tableHeaderData: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    })),
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
};

export default UsersList;
